import React, { useState, useEffect, useRef, useMemo } from 'react';
import Fature from '../../assets/home/featurebg.png';

// Import featured media logos
import featured1 from '../../assets/home/featured/featured1.png';
import featured2 from '../../assets/home/featured/featured2.png';
import featured3 from '../../assets/home/featured/featured3.png';
import featured4 from '../../assets/home/featured/featured4.png';
import featured5 from '../../assets/home/featured/featured5.png';
import featured6 from '../../assets/home/featured/featured6.png';

const FeaturedSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const sectionRef = useRef(null);

  // Media outlets data with SEO-optimized content
  const mediaLogos = useMemo(() => [
    {
      img: featured1,
      alt: "Dainik Jagran - Leading Hindi newspaper featuring RoadExpress logistics services",
      name: "Dainik Jagran",
      description: "Featured in India's largest Hindi daily newspaper"
    },
    {
      img: featured2,
      alt: "Dainik Bhaskar - Major Indian newspaper covering RoadExpress innovations",
      name: "Dainik Bhaskar",
      description: "Covered by one of India's leading newspaper groups"
    },
    {
      img: featured3,
      alt: "Hindustan - Prominent Hindi news publication featuring our logistics solutions",
      name: "Hindustan",
      description: "Featured in prominent Hindi news publication"
    },
    {
      img: featured4,
      alt: "Prabhat Khabar - Regional news outlet highlighting RoadExpress services",
      name: "Prabhat Khabar",
      description: "Highlighted by leading regional news publication"
    },
    {
      img: featured5,
      alt: "Inc42 - Startup news platform featuring RoadExpress as innovative logistics company",
      name: "Inc42",
      description: "Featured on India's leading startup news platform"
    },
    {
      img: featured6,
      alt: "News18 - National news channel covering RoadExpress logistics innovations",
      name: "News18",
      description: "Covered by India's premier news network"
    }
  ], []);

  // Intersection Observer with proper cleanup
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.2,
        rootMargin: '50px'
      }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  // Handle background image loading
  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageError(true);
    console.error('Failed to load featured section background image');
  };

  return (
    <section
      ref={sectionRef}
      className="w-full relative"
      aria-labelledby="featured-section-title"
      role="region"
    >
      {/* Heading Section with border */}
      <header className="w-full bg-white py-12 border-b border-gray-200">
        <h2
          id="featured-section-title"
          className={`text-3xl md:text-4xl font-bold text-center text-gray-800 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
        >
          Featured <span className="text-[#F05A28]">On</span>
        </h2>
        <p
          className={`text-center text-gray-600 mt-4 max-w-2xl mx-auto px-4 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          style={{ transitionDelay: '0.2s' }}
        >
          Trusted by leading media outlets across India for our innovative logistics solutions and reliable service delivery.
        </p>
      </header>

      {/* Background Image Container */}
      <div className="w-full relative min-h-[400px] md:h-[450px] overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          {!imageError ? (
            <>
              <img
                src={Fature}
                alt="Modern logistics and transportation background showcasing RoadExpress infrastructure"
                className={`w-full h-full object-cover transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                onLoad={handleImageLoad}
                onError={handleImageError}
                loading="lazy"
              />
              {/* Loading placeholder */}
              {!imageLoaded && (
                <div className="absolute inset-0 bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 animate-pulse">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-gray-500 text-lg">Loading...</div>
                  </div>
                </div>
              )}
            </>
          ) : (
            // Fallback gradient background
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-[#F05A28]">
              <div
                className="absolute inset-0 opacity-20"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }}
              />
            </div>

          )}

          {/* Dark overlay for better contrast */}
          <div className="absolute inset-0 bg-black/30"></div>
        </div>

        {/* Media Outlet Logos */}
        <div className="absolute inset-0 flex items-center justify-center px-4 sm:px-6 lg:px-8">
          <div
            className="w-full max-w-6xl mx-auto"
            role="region"
            aria-labelledby="media-coverage-title"
          >
            <h3 id="media-coverage-title" className="sr-only">
              Media Coverage and Press Features
            </h3>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6 lg:gap-8 items-center justify-items-center">
              {mediaLogos.map((media, index) => (
                <article
                  key={`media-${index}`}
                  className={`group transform transition-all duration-700 hover:scale-110 focus-within:scale-110 ${isVisible
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-12 opacity-0'
                    }`}
                  style={{
                    transitionDelay: `${index * 0.15}s`
                  }}
                  role="img"
                  aria-labelledby={`media-title-${index}`}
                  aria-describedby={`media-desc-${index}`}
                >
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 md:p-6 transition-all duration-300 hover:bg-white hover:shadow-xl group-hover:shadow-2xl focus-within:bg-white focus-within:shadow-xl">
                    <img
                      src={media.img}
                      alt={media.alt}
                      className="h-6 md:h-8 lg:h-12 w-auto object-contain filter brightness-90 hover:brightness-100 transition-all duration-300 group-hover:scale-105"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        console.error(`Failed to load media logo: ${media.name}`);
                      }}
                    />

                    {/* Hidden content for SEO and accessibility */}
                    <div className="sr-only">
                      <h4 id={`media-title-${index}`}>{media.name}</h4>
                      <p id={`media-desc-${index}`}>{media.description}</p>
                    </div>
                  </div>

                  {/* Tooltip for desktop */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-10 hidden lg:block">
                    {media.name}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                  </div>
                </article>
              ))}
            </div>

            {/* Trust indicators */}
            <div
              className={`mt-8 text-center transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
              style={{ transitionDelay: '1s' }}
            >
              <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8 text-white/90 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" aria-hidden="true"></div>
                  <span>Trusted by 500+ businesses</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" aria-hidden="true"></div>
                  <span>Featured in 50+ publications</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" aria-hidden="true"></div>
                  <span>Industry-leading innovation</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Gradient Fade */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" aria-hidden="true"></div>
      </div>

      {/* Structured Data for SEO (JSON-LD) */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "RoadExpress",
          "description": "Leading logistics and transportation company in India",
          "url": "https://roadexpress.com",
          "mentions": mediaLogos.map(media => ({
            "@type": "NewsMediaOrganization",
            "name": media.name,
            "description": media.description
          })),
          "award": mediaLogos.map(media => media.name).join(", ") + " media coverage",
          "hasCredential": {
            "@type": "EducationalOccupationalCredential",
            "credentialCategory": "Media Recognition",
            "recognizedBy": mediaLogos.map(media => media.name)
          }
        })}
      </script>

      {/* CSS for animations and responsive design */}
      <style jsx>{`
        @media (prefers-reduced-motion: reduce) {
          * {
            transition-duration: 0.01ms !important;
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
          }
        }
        
        @media (max-width: 640px) {
          .grid {
            gap: 1rem;
          }
        }
        
        /* Enhanced hover effects for desktop */
        @media (min-width: 1024px) {
          article:hover .absolute {
            opacity: 1;
          }
        }
      `}</style>
    </section>
  );
};

export default FeaturedSection;